import { FC, useEffect, useState } from 'react';
import { createStyles, makeStyles, Popover } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import classNames from 'classnames';

import { fetchMetricsHealth } from 'src/apis/metricsHealth';

import {
  METRICS_INTERVALS,
  METRICS_TABS,
  MetricsInterval,
  MetricsTabId,
} from './constants';
import {
  AllNotifications,
  InboxNotification,
  NotificationFeed,
} from './NotificationFeed';
import { MOCK_METRICS_NOTIFICATIONS } from './notificationsMock';
import { MetricsChartData } from './types';
import MetricsGraph from './MetricsGraph';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      background: '#EFEDEB',
      fontFamily: 'Roboto, Sans Serif',
    },
    sidebar: {
      width: 240,
      flexShrink: 0,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      borderRight: '1px solid #D8D4CE',
      position: 'sticky',
      top: 0,
      height: '100vh',
      overflowY: 'auto',
    },
    tab: {
      position: 'relative',
      width: '100%',
      minHeight: 56,
      padding: '12px 16px',
      background: '#F7F5F3',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      color: '#202226',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
      textAlign: 'left',
      cursor: 'pointer',
      fontFamily: 'inherit',

      '&:hover': {
        background: '#E4E0D8',
      },
    },
    activeTab: {
      background: '#C8C4BC',

      '&:hover': {
        background: '#C8C4BC',
      },
    },
    badge: {
      position: 'absolute',
      top: -8,
      right: -8,
      minWidth: 20,
      height: 20,
      padding: '0 5px',
      borderRadius: 10,
      background: '#DC5E8A',
      color: '#FFFFFF',
      fontSize: 11,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    allButton: {
      marginTop: 'auto',
    },
    content: {
      flex: 1,
      padding: 32,
      minWidth: 0,
    },
    title: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '28px',
      color: '#202226',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 16,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      gap: 12,
      marginLeft: 'auto',
    },
    notifyButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      height: 32,
      padding: '0 12px',
      background: '#FFFFFF',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      color: '#202226',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      fontFamily: 'inherit',

      '&:hover': {
        background: '#F7F5F3',
      },
    },
    notifyButtonOpen: {
      background: '#E4E0D8',

      '&:hover': {
        background: '#E4E0D8',
      },
    },
    notifyIcon: {
      fontSize: 18,
    },
    notifyCount: {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      background: '#DC5E8A',
      color: '#FFFFFF',
      fontSize: 11,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    popoverPaper: {
      marginTop: 8,
      width: 440,
      maxWidth: 'calc(100vw - 64px)',
      border: '1px solid #C8C4BC',
      borderRadius: 2,
      boxShadow: '0 12px 32px rgba(32, 34, 38, 0.16)',
      overflow: 'hidden',
    },
    charts: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 16,

      '& > *:only-child': {
        gridColumn: '1 / -1',
      },

      '@media (min-width: 1280px)': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
    },
    toggleButton: {
      color: '#202226 !important',
      backgroundColor: '#FFFFFF',
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      padding: '4px 10px',
    },
    toggleButtonSelected: {
      color: '#FFFFFF !important',
      background: 'linear-gradient(0deg, #005FFA -20.98%, #00B5FF 140.22%)',
    },
    status: {
      color: '#5C6168',
      fontSize: 14,
      lineHeight: '20px',
      marginTop: 8,
    },
    error: {
      color: '#B42318',
    },
  })
);

const MetricsDashboard: FC = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<MetricsTabId>(METRICS_TABS[0].id);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [feedAnchor, setFeedAnchor] = useState<HTMLElement | null>(null);
  const [interval, setInterval] = useState<MetricsInterval>('5m');
  const [charts, setCharts] = useState<MetricsChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => new Set());
  const selectedTab = METRICS_TABS.find((tab) => tab.id === activeTab);
  const showSharedInterval =
    activeTab === 'scoring' ||
    activeTab === 'sagemaker' ||
    activeTab === 'database' ||
    activeTab === 'queue' ||
    charts.length > 1;
  const inboxNotifications: InboxNotification[] =
    MOCK_METRICS_NOTIFICATIONS.filter((item) => !deletedIds.has(item.id)).map(
      (item) => ({
        ...item,
        checked: checkedIds.has(item.id),
      })
    );
  const tabNotifications = inboxNotifications.filter(
    (item) => item.tabId === activeTab
  );
  const tabUncheckedCount = tabNotifications.filter(
    (item) => !item.checked
  ).length;
  const uncheckedTotal = inboxNotifications.filter(
    (item) => !item.checked
  ).length;

  const toggleChecked = (id: string) => {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const markChecked = (ids: string[]) => {
    setCheckedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const deleteNotifications = (ids: string[]) => {
    setDeletedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const openTab = (tabId: MetricsTabId) => {
    setShowAllNotifications(false);
    setActiveTab(tabId);
    setFeedAnchor(null);
  };

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    fetchMetricsHealth(activeTab, interval)
      .then((data) => {
        if (!cancelled) {
          setCharts(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCharts([]);
          setError(
            activeTab === 'scoring'
              ? 'Unable to load scoring health. Check the monitoring API and try again.'
              : activeTab === 'sagemaker'
              ? 'Unable to load SageMaker health. Check the monitoring API and try again.'
              : activeTab === 'database'
              ? 'Unable to load database health. Check the monitoring API and try again.'
              : activeTab === 'queue'
              ? 'Unable to load queue health. Check the monitoring API and try again.'
              : 'Unable to load metrics for this tab.'
          );
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, interval]);

  return (
    <div className={classes.root}>
      <nav className={classes.sidebar} aria-label='Metrics sections'>
        {METRICS_TABS.map((tab) => {
          const count = inboxNotifications.filter(
            (item) => item.tabId === tab.id && !item.checked
          ).length;

          return (
            <button
              key={tab.id}
              type='button'
              className={classNames(classes.tab, {
                [classes.activeTab]:
                  !showAllNotifications && tab.id === activeTab,
              })}
              onClick={() => openTab(tab.id)}
              aria-current={
                !showAllNotifications && tab.id === activeTab
                  ? 'page'
                  : undefined
              }
            >
              {tab.label}
              {count > 0 && (
                <span
                  className={classes.badge}
                  aria-label={`${count} unread notifications`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <button
          type='button'
          className={classNames(classes.tab, classes.allButton, {
            [classes.activeTab]: showAllNotifications,
          })}
          onClick={() => {
            setShowAllNotifications(true);
            setFeedAnchor(null);
          }}
          aria-current={showAllNotifications ? 'page' : undefined}
        >
          All notifications
          {uncheckedTotal > 0 && (
            <span
              className={classes.badge}
              aria-label={`${uncheckedTotal} unread notifications`}
            >
              {uncheckedTotal}
            </span>
          )}
        </button>
      </nav>
      <main className={classes.content}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            {showAllNotifications ? 'All notifications' : selectedTab?.label}
          </Typography>
          {!showAllNotifications && (
            <div className={classes.headerActions}>
              {showSharedInterval && (
                <ToggleButtonGroup
                  value={interval}
                  exclusive
                  size='small'
                  onChange={(_, next: MetricsInterval | null) => {
                    if (next) {
                      setInterval(next);
                    }
                  }}
                  aria-label='Time interval'
                >
                  {METRICS_INTERVALS.map((option) => (
                    <ToggleButton
                      key={option.id}
                      value={option.id}
                      disableRipple
                      className={classes.toggleButton}
                      classes={{ selected: classes.toggleButtonSelected }}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <button
                type='button'
                className={classNames(classes.notifyButton, {
                  [classes.notifyButtonOpen]: Boolean(feedAnchor),
                })}
                aria-haspopup='dialog'
                aria-expanded={Boolean(feedAnchor)}
                onClick={(event) => {
                  setFeedAnchor((current) =>
                    current ? null : event.currentTarget
                  );
                }}
              >
                <NotificationsNoneIcon className={classes.notifyIcon} />
                Notifications
                {tabUncheckedCount > 0 && (
                  <span className={classes.notifyCount}>
                    {tabUncheckedCount}
                  </span>
                )}
              </button>
              <Popover
                open={Boolean(feedAnchor)}
                anchorEl={feedAnchor}
                onClose={() => setFeedAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ className: classes.popoverPaper }}
              >
                <NotificationFeed
                  title={selectedTab?.label || 'Notifications'}
                  notifications={tabNotifications}
                  onToggleChecked={toggleChecked}
                  onMarkChecked={markChecked}
                  onDelete={(id) => deleteNotifications([id])}
                  onDeleteMany={deleteNotifications}
                  onViewAll={() => {
                    setFeedAnchor(null);
                    setShowAllNotifications(true);
                  }}
                />
              </Popover>
            </div>
          )}
        </div>
        {showAllNotifications ? (
          <AllNotifications
            notifications={inboxNotifications}
            onToggleChecked={toggleChecked}
            onMarkChecked={markChecked}
            onDelete={(id) => deleteNotifications([id])}
            onDeleteMany={deleteNotifications}
          />
        ) : (
          <>
            {error && (
              <Typography className={classNames(classes.status, classes.error)}>
                {error}
              </Typography>
            )}
            {!error && isLoading && (
              <Typography className={classes.status}>
                Loading metrics…
              </Typography>
            )}
            {!error && !isLoading && charts.length === 0 && (
              <Typography className={classes.status}>
                No metrics returned for this interval.
              </Typography>
            )}
            <div className={classes.charts}>
              {!isLoading &&
                charts.map((chart) => (
                  <MetricsGraph
                    key={chart.id || chart.title}
                    data={chart}
                    interval={interval}
                    onIntervalChange={setInterval}
                    showIntervalToggle={!showSharedInterval}
                  />
                ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default MetricsDashboard;
